import React, { useState, useMemo, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, useTheme, Grid } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GrowingStatsSection from "./GrowingStatsSection";
import { palette } from "../../components/common/Palette";
import useDebounce from "../../hooks/useDebounce";
import { useGetServiceKeywords, useGetAllServiceChipKeywords, SearchKeywordItem } from "../../api-data/service-provider/search-keyword";
import { SuggestionItem } from "../../new-components/common/SearchBar";
import { useSearchServiceCategories } from "../../api-data/service-provider/service-categories";
import { useForm, FormProvider } from "react-hook-form";
import PopularSearches from "../../new-components/common/PopularSearches";
import { SEO } from "../../components/common/SEO";
import { useGetAllBanners } from "../../api-data/service-provider/home-screen-images";
import HomeBannerCarousel from "../../components/common/HomeBannerCarousel";
import FooterBannerCard from "../../components/common/FooterBannerCard";
import HomeSearchDialog from "./HomeSearchDialog";
import OpenHelpRequestsPopup from "../admin/help-center-inbox/OpenHelpRequestsPopup";

import { useLocation } from "../../context/NewLocationContext";
import { useTrackEvent } from "../../hooks/useTrackEvent";
import { EventType } from "../../api-data/reporting/enums";
import { buildSearchUrl } from "../../utils/urlHelpers";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    selectedCity,
    selectedLocality,
    selectedCityId,
    selectedLocalityId,
    openLocationDialog,
  } = useLocation();
  const { trackEvent } = useTrackEvent();
  const lastTrackedSearchRef = useRef<string>("");

  const [searchParams, setSearchParams] = useSearchParams();
  const keywordFromURL = searchParams.get("query") || "";

  const [searchInput, setSearchInput] = useState<string>("");
  const [userInteracted, setUserInteracted] = useState<boolean>(false);

  const methods = useForm({
    mode: "onChange",
  });

  const { trigger } = methods;

  useEffect(() => {
    if (keywordFromURL) {
      setSearchInput(keywordFromURL);
      setUserInteracted(false);
    }

    if (searchParams.get("focus") === "true") {
      const searchInputElem = document.querySelector<HTMLInputElement>(
        "input[data-searchbar]",
      );
      if (searchInputElem) {
        setTimeout(() => {
          searchInputElem.focus();
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete("focus");
              return next;
            },
            { replace: true },
          );
        }, 500);
      }
    }
  }, [keywordFromURL, searchParams, setSearchParams]);

  const debouncedSearchInput = useDebounce(searchInput, 300);

  const shouldFetch = debouncedSearchInput.trim().length >= 3;
  const isTypingButNotDebounced =
    searchInput.trim().length >= 3 && debouncedSearchInput !== searchInput;

  const { data: keywords, isFetching: keywordsLoading } = useGetServiceKeywords(
    shouldFetch ? debouncedSearchInput : "",
  );

  // Fetch only keywords flagged with showAsServiceChip = true for the typewriter placeholder.
  const { data: serviceChipKeywords, isLoading: allKeywordsLoading } =
    useGetAllServiceChipKeywords();
  const typewriterKeywords: string[] | undefined =
    serviceChipKeywords && serviceChipKeywords.length > 0
      ? serviceChipKeywords.map((item) => item.localized?.keyword ?? item.keyword)
      : undefined;

  const { data: categoriesResponse, isFetching: categoriesLoading } =
    useSearchServiceCategories(100, 0, shouldFetch ? debouncedSearchInput : "", selectedCity);

  const suggestions = useMemo<SuggestionItem[]>(() => {
    if (!shouldFetch) return [];

    const categories =
      categoriesResponse?.pages?.flatMap((p: any) => p.data) || [];
    const seen = new Set<string>();
    const items: SuggestionItem[] = [];

    keywords?.data?.forEach((item: SearchKeywordItem) => {
      const label = item.localized?.keyword ?? item.keyword;
      if (label && !seen.has(label)) {
        seen.add(label);
        const catName = item.localized?.categoryName ?? item.categoryName ?? null;
        items.push({ label, categoryName: catName });
      }
    });

    categories.forEach((item: any) => {
      const label = item.localized?.categoryName ?? item.categoryName;
      if (label && !seen.has(label)) {
        seen.add(label);
        items.push({ label });
      }
    });

    return items;
  }, [keywords, categoriesResponse, shouldFetch]);

  // categoryId only from a category tap (OOTWUI-006); typed searches omit it.
  const handleSearch = (keyword?: string, categoryId?: number) => {
    const query = keyword?.trim();
    // Navigate using new hierarchical URL structure
    // If query is empty, just navigate to city page
    const searchUrl = buildSearchUrl(
      selectedCity,
      selectedLocality,
      query || undefined,
    );
    navigate(searchUrl, categoryId ? { state: { categoryId } } : undefined);
  };

  const handleSearchWithTracking = (keyword?: string) => {
    const searchQuery = keyword || searchInput;
    const trackingKey = `${searchQuery}|${selectedCityId}|${selectedLocalityId}`;

    // Prevent duplicate tracking of the same search
    if (lastTrackedSearchRef.current !== trackingKey) {
      lastTrackedSearchRef.current = trackingKey;
      trackEvent(EventType.SEARCH_PERFORMED, {
        keyword: searchQuery,
        cityId: selectedCityId,
        localityId: selectedLocalityId,
        source: "home_search",
      });
    }
    handleSearch(keyword);
  };

  const onSubmit = async () => {
    if (selectedCity && (await trigger())) {
      handleSearchWithTracking(searchInput);
    }
  };

  const { data: allBanners = [], isFetching: bannersLoading } = useGetAllBanners();
  const bannerImages   = allBanners.filter((b) => !b.isFooterBanner);
  const footerBanners  = allBanners.filter((b) => b.isFooterBanner);

  return (
    <>
      <OpenHelpRequestsPopup />
      <SEO
        title="OnlyOneTap - Find Trusted Local Service Providers for Home Services"
        description="OnlyOneTap connects you with reliable local service providers for cleaning, plumbing, electrician services, and more household needs. Book trusted professionals in just one tap."
        keywords="home services, local service providers, cleaning services, plumbing services, electrician, household services, home repair, OnlyOneTap, service booking"
        url="/"
      />
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          sx={{ backgroundColor: palette.information.main }}
        >
          {isMobile ? (
            <>
              <Box sx={{ position: "relative" }}>
                <HomeBannerCarousel
                  images={bannerImages}
                  isMobile
                  loading={bannersLoading}
                />
              </Box>
              <Grid
                container
                justifyContent="center"
                sx={{
                  position: "relative",
                  zIndex: 10,
                  mt: -8,
                  px: 2,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
                  <Typography component="h1" sx={visuallyHidden}>
                    {t("home.heroTitle")}
                  </Typography>
                  <HomeSearchDialog
                    city={selectedCity}
                    locality={selectedLocality}
                    searchValue={searchInput}
                    suggestions={userInteracted ? suggestions : []}
                    loadingSuggestions={
                      userInteracted &&
                      (isTypingButNotDebounced ||
                        keywordsLoading ||
                        categoriesLoading)
                    }
                    allowDropdown={userInteracted}
                    disableSearch={!selectedCity}
                    onSearchChange={(val) => {
                      setSearchInput(val);
                      setUserInteracted(true);
                    }}
                    onSearchSelect={async (val) => {
                      setSearchInput(val);
                      setUserInteracted(true);
                      if (selectedCity && (await trigger()))
                        handleSearchWithTracking(val);
                    }}
                    typewriterKeywords={typewriterKeywords}
                    typewriterKeywordsLoading={allKeywordsLoading}
                    onChangeLocationClick={() => openLocationDialog("home_page")}
                    categories={
                      categoriesResponse?.pages?.flatMap((p: any) => p.data) || []
                    }
                    keywords={keywords?.data || []}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                justifyContent="center"
                sx={{ position: "relative", zIndex: 10, mt: 3, px: 2, width: "100%", boxSizing: "border-box" }}
              >
                <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
                  <PopularSearches
                    currentCity={selectedCity}
                    currentLocality={selectedLocality}
                    currentCityId={selectedCityId}
                    onSelect={async (keyword, categoryId) => {
                      if (selectedCity && (await trigger())) {
                        handleSearch(keyword, categoryId);
                      }
                    }}
                  />
                </Grid>
              </Grid>

              {/* Footer banner + KPI stats */}
              <Grid container justifyContent="center" sx={{ px: 2, width: "100%", boxSizing: "border-box" }}>
                <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
                  <FooterBannerCard banners={footerBanners} isMobile />
                  <GrowingStatsSection variant="mobile" />
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              {/* ===== Hero band — two-column: headline+search-card | promo card ===== */}
              <Box
                sx={{
                  background: `linear-gradient(150deg, #21a99f 0%, #1a8f86 55%, ${theme.palette.primary.dark} 120%)`,
                }}
              >
                <Box
                  sx={{
                    maxWidth: 1240,
                    mx: "auto",
                    px: 4,
                    pt: 7,
                    pb: 8,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 6,
                    alignItems: "center",
                    boxSizing: "border-box",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <Typography
                      component="h1"
                      sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1, color: "secondary.main", letterSpacing: "-0.5px" }}
                    >
                      {t("home.heroTitle")}
                    </Typography>
                    <Typography sx={{ fontSize: 16, lineHeight: 1.5, color: "rgba(255,255,255,.9)", maxWidth: 440, mt: 1.25 }}>
                      {t("home.heroSubtitle")}
                    </Typography>
                    <Box sx={{ mt: 2.5, maxWidth: 520 }}>
                      <HomeSearchDialog
                        city={selectedCity}
                        locality={selectedLocality}
                        searchValue={searchInput}
                        suggestions={userInteracted ? suggestions : []}
                        loadingSuggestions={
                          userInteracted &&
                          (isTypingButNotDebounced ||
                            keywordsLoading ||
                            categoriesLoading)
                        }
                        allowDropdown={userInteracted}
                        disableSearch={!selectedCity}
                        onSearchChange={(val) => {
                          setSearchInput(val);
                          setUserInteracted(true);
                        }}
                        onSearchSelect={async (val) => {
                          setSearchInput(val);
                          setUserInteracted(true);
                          if (selectedCity && (await trigger()))
                            handleSearchWithTracking(val);
                        }}
                        typewriterKeywords={typewriterKeywords}
                        typewriterKeywordsLoading={allKeywordsLoading}
                        onChangeLocationClick={() => openLocationDialog("home_page")}
                        categories={
                          categoriesResponse?.pages?.flatMap((p: any) => p.data) || []
                        }
                        keywords={keywords?.data || []}
                      />
                    </Box>
                  </Box>

                  <HomeBannerCarousel images={bannerImages} isMobile={false} loading={bannersLoading} />
                </Box>
              </Box>

              {/* ===== Main content ===== */}
              <Box sx={{ maxWidth: 1240, mx: "auto", px: 4, pt: 7, pb: 3, boxSizing: "border-box" }}>
                <PopularSearches
                  currentCity={selectedCity}
                  currentLocality={selectedLocality}
                  currentCityId={selectedCityId}
                  onSelect={async (keyword, categoryId) => {
                    if (selectedCity && (await trigger())) {
                      handleSearch(keyword, categoryId);
                    }
                  }}
                />

                {/* Footer banner */}
                <FooterBannerCard banners={footerBanners} />

                {/* KPI stats — live data from /public/home-stats */}
                <GrowingStatsSection variant="desktop" />
              </Box>
            </>
          )}
        </Box>
      </FormProvider>
    </>
  );
};

export default HomePage;
